import React, { memo, useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js';

import * as C from './Charts.styled';
import {
  CHART_GRID,
  CHART_INK,
  CHART_NO_ANIMATION,
  CHART_TICK_FONT,
  chartBaseTooltip,
  chartFaded,
  chartLegend,
} from './chartTokens';
import { registerCharts } from './registerCharts';

registerCharts();

const DASH = '—';

/** Ряд смешанного графика: столбцы или линия, своя ось. */
export interface IComboSeries {
  label: string;
  /** По подписям; `null` — разрыв / нет столбика (не ноль). */
  data: ReadonlyArray<number | null>;
  type: 'bar' | 'line';
  axis: 'left' | 'right';
  color: string;
  /** Группа стека столбцов; без неё столбцы стоят рядом. */
  stack?: string | null;
  /** Area: заливка под линией. */
  fill?: boolean;
}

export interface IComboAxis {
  /** Подпись оси: единица / валюта её рядов. */
  title?: string | null;
  format: (value: number) => string;
}

export interface IComboDataset {
  type: 'bar' | 'line';
  label: string;
  data: Array<number | null>;
  yAxisID: 'y' | 'y1';
  backgroundColor: string;
  borderColor: string;
  borderWidth: number;
  /** Столбцы — стек по группе; ряды без группы — свой стек (рядом). */
  stack?: string;
  order: number;
  borderRadius?: number;
  maxBarThickness?: number;
  pointRadius?: number;
  tension?: number;
  spanGaps?: false;
  fill?: 'origin' | false;
}

/**
 * ТЗ 0113 — данные смешанного графика: КАЖДЫЙ ряд (сколько угодно столбцов
 * и линий), каждый на своей оси. Столбцы одной группы `stack` — стеком,
 * без группы — рядом; линии поверх столбцов (`order`).
 */
export const comboSeriesData = (
  labels: ReadonlyArray<string>,
  series: ReadonlyArray<IComboSeries>,
): { labels: string[]; datasets: IComboDataset[] } => ({
  labels: [...labels],
  datasets: series.map((item, index) => {
    const base = {
      label: item.label,
      data: labels.map((_, i) => item.data[i] ?? null),
      yAxisID: item.axis === 'right' ? ('y1' as const) : ('y' as const),
      borderColor: item.color,
    };
    if (item.type === 'bar') {
      return {
        ...base,
        type: 'bar' as const,
        backgroundColor: item.color,
        borderWidth: 0,
        stack: item.stack ? `stack:${item.stack}` : `own:${index}`,
        order: 2,
        borderRadius: 3,
        maxBarThickness: 36,
      };
    }
    return {
      ...base,
      type: 'line' as const,
      backgroundColor: item.fill ? chartFaded(item.color, 0.25) : item.color,
      borderWidth: 2,
      order: 1,
      pointRadius: 2,
      tension: 0,
      spanGaps: false as const,
      fill: item.fill ? ('origin' as const) : false,
    };
  }),
});

/** Есть ли столбцы со стеком (общая группа у двух и больше рядов). */
export const comboHasStack = (series: ReadonlyArray<IComboSeries>): boolean => {
  const counts = new Map<string, number>();
  series.forEach((item) => {
    if (item.type === 'bar' && item.stack) {
      counts.set(item.stack, (counts.get(item.stack) ?? 0) + 1);
    }
  });
  return Array.from(counts.values()).some((count) => count > 1);
};

export interface IComboSeriesChartProps {
  labels: ReadonlyArray<string>;
  series: ReadonlyArray<IComboSeries>;
  /** Левая ось — формат и подпись по её рядам. */
  left: IComboAxis;
  /** Правая ось; без рядов справа не рисуется. */
  right?: IComboAxis | null;
  /** Горизонтальные столбцы (только без линий). */
  horizontal?: boolean;
  ariaLabel?: string;
}

/**
 * ТЗ 0113 — смешанный график: любые ряды столбцов и линий на левой и правой
 * оси. Формат подсказки — по оси ряда (у левой и правой свои единицы и
 * валюты, не конвертируются); оси подписаны единицами своих рядов.
 */
export const ComboSeriesChart = memo(
  ({
    labels,
    series,
    left,
    right,
    horizontal = false,
    ariaLabel,
  }: IComboSeriesChartProps) => {
    const data = useMemo(() => comboSeriesData(labels, series), [
      labels,
      series,
    ]);
    const hasRight = series.some((item) => item.axis === 'right');
    const stacked = comboHasStack(series);
    const options: ChartOptions<'bar'> = useMemo(() => {
      const valueAxis = (axis: IComboAxis, side: 'left' | 'right') => ({
        position: side,
        stacked,
        beginAtZero: true,
        grid:
          side === 'left' ? { color: CHART_GRID } : { drawOnChartArea: false },
        ...(axis.title
          ? {
              title: {
                display: true,
                text: axis.title,
                color: CHART_INK,
                font: CHART_TICK_FONT,
              },
            }
          : {}),
        ticks: {
          font: CHART_TICK_FONT,
          color: CHART_INK,
          callback: (value: string | number) => axis.format(Number(value)),
        },
      });
      const labelAxis = {
        stacked,
        grid: { display: false },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 16,
          font: CHART_TICK_FONT,
          color: CHART_INK,
        },
      };
      const leftAxis = valueAxis(left, 'left');
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: CHART_NO_ANIMATION,
        indexAxis: horizontal ? 'y' : 'x',
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { ...chartLegend, display: series.length > 1 },
          tooltip: {
            ...chartBaseTooltip,
            callbacks: {
              label: (item) => {
                const raw = item.raw as number | null;
                const axis =
                  (item.dataset as { yAxisID?: string }).yAxisID === 'y1' &&
                  right
                    ? right
                    : left;
                return `${item.dataset.label}: ${
                  raw === null ? DASH : axis.format(Number(raw))
                }`;
              },
            },
          },
        },
        scales: horizontal
          ? { y: labelAxis, x: { ...leftAxis, position: 'bottom' } }
          : {
              x: labelAxis,
              y: leftAxis,
              ...(hasRight && right ? { y1: valueAxis(right, 'right') } : {}),
            },
      } as ChartOptions<'bar'>;
    }, [left, right, hasRight, stacked, horizontal, series.length]);
    return (
      <C.ChartBox role="img" aria-label={ariaLabel}>
        <Chart type="bar" data={data as never} options={options as never} />
      </C.ChartBox>
    );
  },
);

ComboSeriesChart.displayName = 'ComboSeriesChart';
